/**
 * useKeyboard — Keyboard navigation helpers for accessibility.
 *
 * Provides:
 * - Arrow key navigation within lists/menus
 * - Escape to close overlays
 * - Focus trapping for modal dialogs
 *
 * Usage:
 *   const { onKeyDown } = useListKeyboard({
 *     items: menuItems,
 *     activeIndex,
 *     onSelect: (index) => handleSelect(index),
 *     onEscape: () => closeMenu(),
 *   });
 */

import { useCallback, useRef, useEffect } from 'react';

interface UseListKeyboardOptions {
  /** Total number of items in the list */
  itemCount: number;
  /** Currently focused/active index */
  activeIndex: number;
  /** Callback when active index changes */
  onActiveChange: (index: number) => void;
  /** Callback when Enter/Space is pressed on active item */
  onSelect?: (index: number) => void;
  /** Callback when Escape is pressed */
  onEscape?: () => void;
  /** Whether the list is vertical (ArrowUp/Down) or horizontal (ArrowLeft/Right) */
  orientation?: 'vertical' | 'horizontal';
  /** Whether to wrap around at edges */
  wrap?: boolean;
}

export function useListKeyboard({
  itemCount,
  activeIndex,
  onActiveChange,
  onSelect,
  onEscape,
  orientation = 'vertical',
  wrap = true,
}: UseListKeyboardOptions) {
  const prevKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
  const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case prevKey: {
          e.preventDefault();
          const next = activeIndex > 0 ? activeIndex - 1 : wrap ? itemCount - 1 : 0;
          onActiveChange(next);
          break;
        }
        case nextKey: {
          e.preventDefault();
          const next = activeIndex < itemCount - 1 ? activeIndex + 1 : wrap ? 0 : itemCount - 1;
          onActiveChange(next);
          break;
        }
        case 'Home': {
          e.preventDefault();
          onActiveChange(0);
          break;
        }
        case 'End': {
          e.preventDefault();
          onActiveChange(itemCount - 1);
          break;
        }
        case 'Enter':
        case ' ': {
          e.preventDefault();
          onSelect?.(activeIndex);
          break;
        }
        case 'Escape': {
          e.preventDefault();
          onEscape?.();
          break;
        }
      }
    },
    [activeIndex, itemCount, nextKey, onActiveChange, onEscape, onSelect, prevKey, wrap]
  );

  return { onKeyDown };
}

/**
 * useFocusTrap — Traps focus within a container (for modals, panels, dialogs).
 */
export function useFocusTrap(active: boolean) {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active || !containerRef.current) return;

    // Save the previously focused element to restore later
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Focus the first focusable element
    const focusable = containerRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length > 0) {
      focusable[0].focus();
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !containerRef.current) return;

      const focusableEls = containerRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableEls.length === 0) return;

      const firstEl = focusableEls[0];
      const lastEl = focusableEls[focusableEls.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the previously focused element
      previousFocusRef.current?.focus();
    };
  }, [active]);

  return containerRef;
}

export default useListKeyboard;
